import { getSharePointToken } from "../../shell/auth/tokens";
import { appConfig } from "../config/appConfig";
import { isRetryableStatus, backoffMs } from "./throttle";

/**
 * SharePoint REST API client.
 *
 * This is the SWA equivalent of `this.context.spHttpClient` from SPFx.
 * The calling code in your CRM module will look nearly identical:
 *
 *   SPFx:
 *     const r = await this.props.context.spHttpClient.get(
 *       `${siteUrl}/_api/web/lists/getbytitle('CRM_Customers')/items?...`,
 *       SPHttpClient.configurations.v1
 *     );
 *     const json = await r.json();
 *
 *   SWA (this wrapper):
 *     const items = await sp.getListItems('CRM_Customers', {
 *       select: 'Id,RawLocation,IslandGroup',
 *       top: 5000
 *     });
 */

export interface SPQueryOptions<T = any> {
  select?: string;
  filter?: string;
  orderby?: string;
  top?: number;
  expand?: string;
  /**
   * Return ONLY the first page instead of following SharePoint's `__next`
   * link to exhaustion.
   *
   * `top` is a PAGE SIZE to SharePoint, not a limit: a query with `top: 1`
   * against a matching set of 30,000 rows still yields all 30,000 — one row
   * per HTTP request — because the pager keeps following `__next`. Any caller
   * that only needs "the first N" (existence probes above all) must set this,
   * or it silently issues thousands of requests and gets throttled.
   */
  singlePage?: boolean;
  /**
   * Called after each page lands, with the running row total AND that page's
   * rows.
   *
   * A read of a 34,000-row list is seven sequential round trips with nothing
   * on screen, and "it is still going" is not something a caller can say
   * without this. The ROWS were added on 2026-09-18 so a caller can do better
   * than say it: the BQC1 board paints the first 5,000 while the other six
   * pages are still in flight, which is the difference between a table that
   * appears and a table that arrives.
   *
   * It is a REPORT, not a control: returning nothing and throwing nothing is
   * the contract, and the pager never waits on it. `page` is the rows from
   * THIS page only — the caller accumulates if it wants to, and the finished
   * array is still returned whole.
   */
  onPage?: (rowsSoFar: number, page: T[]) => void;
}

interface SPItemsResponseVerbose<T> {
  d: {
    results: T[];
    __next?: string;
  };
}

// The retry policy lives in throttle.ts so Graph callers can share it without
// importing this client (and with it the app config). Re-exported here because
// every existing caller imports it from spClient.
export { isRetryableStatus, backoffMs } from "./throttle";

class SharePointClient {
  constructor(private readonly siteUrl: string) {}

  /**
   * Under sustained load SharePoint sometimes stops answering with a plain
   * 429 and instead redirects the request to its own `_layouts/15/Throttle.htm`
   * interstitial — a page with no CORS headers, so the browser blocks it
   * before `fetch()` ever resolves to a Response at all. That surfaces here
   * as a rejected promise (a generic "Failed to fetch"/CORS TypeError), not
   * a status code — so it has to be caught and retried at the network level
   * too, or a request that looks exactly like heavy throttling just fails
   * outright on the very first attempt instead of backing off like every
   * other throttle response does.
   */
  private async fetch<T>(url: string, init?: RequestInit, maxTries = 6): Promise<T> {
    let attempt = 0;
    for (;;) {
      const token = await getSharePointToken();
      let response: Response;
      try {
        response = await fetch(url, {
          ...init,
          headers: {
            Authorization: `Bearer ${token}`,
            // SharePoint on this tenant rejects odata=nometadata with a 406 on
            // queries that span multiple pages or use certain field selections.
            // The verbose flavor is the most compatible across SP tenants.
            Accept: "application/json;odata=verbose",
            "Content-Type": "application/json;odata=verbose",
            ...(init?.headers ?? {})
          }
        });
      } catch (e) {
        attempt++;
        if (attempt >= maxTries) throw e instanceof Error ? e : new Error(String(e));
        await new Promise(res => setTimeout(res, backoffMs(attempt, null)));
        continue;
      }

      if (isRetryableStatus(response.status)) {
        attempt++;
        if (attempt >= maxTries) {
          const text = await response.text().catch(() => "");
          throw new Error(
            `SharePoint request failed: ${response.status} ${response.statusText}\n${url}\n${text}`
          );
        }
        await new Promise(res => setTimeout(res, backoffMs(attempt, response.headers.get("Retry-After"))));
        continue;
      }

      if (!response.ok) {
        const text = await response.text().catch(() => "");
        throw new Error(
          `SharePoint request failed: ${response.status} ${response.statusText}\n${url}\n${text}`
        );
      }

      // SharePoint's POST + X-HTTP-Method: MERGE/DELETE override (used by
      // updateItem/deleteItem) doesn't reliably return 204 for an empty body —
      // on this tenant it comes back as 200 with nothing to parse. Check the
      // actual body text rather than trusting the status code, or
      // response.json() throws "Unexpected end of JSON input" on every
      // update/delete.
      const text = await response.text();
      if (!text) {
        return undefined as T;
      }
      return JSON.parse(text) as T;
    }
  }

  private buildQueryString(options?: SPQueryOptions): string {
    if (!options) return "";
    const params = new URLSearchParams();
    if (options.select) params.set("$select", options.select);
    if (options.filter) params.set("$filter", options.filter);
    if (options.orderby) params.set("$orderby", options.orderby);
    if (options.top !== undefined) params.set("$top", String(options.top));
    if (options.expand) params.set("$expand", options.expand);
    const qs = params.toString();
    return qs ? `?${qs}` : "";
  }

  /**
   * Get items from a list by title, with optional OData query params.
   * Automatically pages through nextLink if the result spans multiple pages.
   *
   * Performance note: if `top` is not provided, we default to the maximum
   * page size (5000) rather than SharePoint's default of 100. For large lists
   * this is the difference between ~2 round-trips and ~75 round-trips.
   *
   * Pass `singlePage: true` when you only want the first page — `top` alone
   * does NOT cap the result (see SPQueryOptions.singlePage).
   */
  async getListItems<T = Record<string, unknown>>(
    listTitle: string,
    options?: SPQueryOptions<T>
  ): Promise<T[]> {
    const effectiveOptions: SPQueryOptions<T> = {
      ...options,
      top: options?.top ?? 5000
    };
    const qs = this.buildQueryString(effectiveOptions);
    let url = `${this.siteUrl}/_api/web/lists/getbytitle('${encodeURIComponent(listTitle)}')/items${qs}`;
    const all: T[] = [];

    while (url) {
      const page = await this.fetch<SPItemsResponseVerbose<T>>(url);
      all.push(...page.d.results);
      options?.onPage?.(all.length, page.d.results);
      url = options?.singlePage ? "" : (page.d.__next ?? "");
    }

    return all;
  }

  /** Get a single item by ID. */
  async getItem<T = Record<string, unknown>>(
    listTitle: string,
    itemId: number,
    options?: SPQueryOptions
  ): Promise<T> {
    const qs = this.buildQueryString(options);
    const url = `${this.siteUrl}/_api/web/lists/getbytitle('${encodeURIComponent(listTitle)}')/items(${itemId})${qs}`;
    const response = await this.fetch<{ d: T }>(url);
    return response.d;
  }

  /** Create a new list item. */
  async createItem<T = Record<string, unknown>>(
    listTitle: string,
    body: Record<string, unknown>
  ): Promise<T> {
    const url = `${this.siteUrl}/_api/web/lists/getbytitle('${encodeURIComponent(listTitle)}')/items`;
    const response = await this.fetch<{ d: T }>(url, {
      method: "POST",
      body: JSON.stringify(body)
    });
    return response.d;
  }

  /**
   * Update an existing list item (MERGE semantics).
   * SharePoint's REST API doesn't truly support HTTP PATCH — it requires
   * POST + X-HTTP-Method: MERGE header instead.
   */
  async updateItem(
    listTitle: string,
    itemId: number,
    body: Record<string, unknown>
  ): Promise<void> {
    const url = `${this.siteUrl}/_api/web/lists/getbytitle('${encodeURIComponent(listTitle)}')/items(${itemId})`;
    await this.fetch<void>(url, {
      method: "POST",
      headers: {
        "If-Match": "*",
        "X-HTTP-Method": "MERGE"
      },
      body: JSON.stringify(body)
    });
  }

  /** Delete a list item. */
  async deleteItem(listTitle: string, itemId: number): Promise<void> {
    const url = `${this.siteUrl}/_api/web/lists/getbytitle('${encodeURIComponent(listTitle)}')/items(${itemId})`;
    await this.fetch<void>(url, {
      method: "POST",
      headers: {
        "If-Match": "*",
        "X-HTTP-Method": "DELETE"
      }
    });
  }

  /**
   * Download a file's raw bytes (e.g. a PDF) by server-relative URL. Used to
   * inspect generated documents — JSON helpers above can't return binary.
   */
  async getFileBytes(serverRelativeUrl: string): Promise<ArrayBuffer> {
    const token = await getSharePointToken();
    const url =
      `${this.siteUrl}/_api/web/GetFileByServerRelativeUrl('${serverRelativeUrl.replace(/'/g, "''")}')/$value`;
    const response = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!response.ok) {
      throw new Error(`SharePoint file download failed: ${response.status} ${serverRelativeUrl}`);
    }
    return response.arrayBuffer();
  }

  /** Escape hatch for arbitrary REST calls not covered above. */
  async raw<T>(pathOrUrl: string, init?: RequestInit): Promise<T> {
    const url = pathOrUrl.startsWith("http")
      ? pathOrUrl
      : `${this.siteUrl}${pathOrUrl.startsWith("/") ? "" : "/"}${pathOrUrl}`;
    return this.fetch<T>(url, init);
  }
}

/** Default client pointed at the site from env config. */
export const sp = new SharePointClient(appConfig.sharepoint.siteUrl);

/** Create a client for a different site if you need cross-site queries. */
export function createSharePointClient(siteUrl: string): SharePointClient {
  return new SharePointClient(siteUrl);
}
